import type { ActivityCalendar } from '@linktivity/ars-neo';
import { convertDateToString } from '@link/share/utils';

/*
 * Which dates the availability calendar asks for, and how the pages of it
 * join. From share's `useActivityCalendar`, unchanged in behaviour.
 */

export type CalendarDay = NonNullable<
  ActivityCalendar['dailyAvailabilities']
>[number];

/** The first request covers this month and the next three. */
export const INITIAL_MONTHS = 4;
/** Each "load more" adds three months. */
export const MORE_MONTHS = 3;

export interface DateRange {
  from: Date;
  to: Date;
}

// The last day of the month `offsetMonths` after `from`'s.
// e.g. from=2026-03-24, offsetMonths=3 → 2026-06-30
const endOfNthMonth = (from: Date, offsetMonths: number) =>
  new Date(from.getFullYear(), from.getMonth() + offsetMonths + 1, 0);

/** Today through the end of the fourth month. */
export function initialRange(today: Date): DateRange {
  return { from: today, to: endOfNthMonth(today, INITIAL_MONTHS - 1) };
}

/** The day after `loadedUntil` through the end of the third month. */
export function nextRange(loadedUntil: Date): DateRange {
  const from = new Date(loadedUntil);
  from.setDate(from.getDate() + 1);
  return { from, to: endOfNthMonth(from, MORE_MONTHS - 1) };
}

/**
 * Whether there may be more to load: the API returned days up to the end of
 * the requested range. A product whose sales end earlier stops short of it.
 */
export function reachesEnd(days: CalendarDay[], to: Date): boolean {
  let last = '';
  for (const day of days) {
    if (day.date && day.date > last) last = day.date;
  }
  return !!last && last >= convertDateToString(to);
}

/** Join two pages of days, by date, the newer page winning, in date order. */
export function mergeByDate(
  existing: CalendarDay[],
  incoming: CalendarDay[]
): CalendarDay[] {
  const byDate = new Map<string, CalendarDay>();
  for (const day of [...existing, ...incoming]) {
    if (day.date) byDate.set(day.date, day);
  }
  return [...byDate.values()].sort((a, b) =>
    (a.date ?? '').localeCompare(b.date ?? '')
  );
}

/** A unit as a day of the calendar sells it, with that day's price. */
export type DayUnit = NonNullable<
  NonNullable<CalendarDay['planInfos']>[number]['unitItems']
>[number];
